import { getCurrency, isExpression, parseAmountInput, toMinor } from '@esportsbudget/shared';
import { type ReactNode, forwardRef, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import { useFormatters } from '@/lib/format';

import { Field, inputClasses } from './Input';

export interface MoneyInputProps {
  value: string;
  onChange: (value: string) => void;
  currency: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Крупное поле для главного сценария — начисления. */
  large?: boolean;
  className?: string;
  onEnter?: () => void;
}

/**
 * Поле ввода денег.
 *
 * Принимает не только число, но и выражение: «60000-5000», «24000*2».
 * Разбор делает общий с сервером код, поэтому результат совпадает с тем,
 * что в итоге запишется в базу.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  {
    value,
    onChange,
    currency,
    label,
    hint,
    error,
    placeholder = '0',
    disabled,
    autoFocus,
    large,
    className,
    onEnter,
  },
  ref,
) {
  const { t } = useTranslation(['validation', 'common']);
  const format = useFormatters();
  const inputId = useId();
  const info = getCurrency(currency);

  const parsed = useMemo(() => parseAmountInput(value), [value]);
  const invalid = value.trim() !== '' && parsed === null;
  const showResult = value.trim() !== '' && parsed !== null && isExpression(value);

  const computedHint = invalid
    ? null
    : showResult
      ? `= ${format.money(toMinor(parsed, currency), currency)}`
      : hint;

  return (
    <Field
      label={label}
      hint={computedHint}
      error={error ?? (invalid ? t('validation:invalidExpression') : null)}
      htmlFor={inputId}
    >
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          autoFocus={autoFocus}
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && onEnter) {
              event.preventDefault();
              onEnter();
            }
          }}
          className={cn(
            inputClasses(invalid || Boolean(error), 'tabular pr-14'),
            large && 'h-[54px] text-[26px] font-semibold tracking-tight',
            className,
          )}
        />
        <span
          className={cn(
            'pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 font-medium text-subtle',
            large ? 'text-[15px]' : 'text-[13px]',
          )}
        >
          {info.symbol}
        </span>
      </div>
    </Field>
  );
});

/** Значение поля в минорных единицах. null — если ввод пустой или некорректный. */
export function moneyInputToMinor(value: string, currency: string): number | null {
  const parsed = parseAmountInput(value);
  if (parsed === null) return null;
  return toMinor(parsed, currency);
}
